import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostAccountingTypeDialogComponent } from './cost-accounting-type-dialog/cost-accounting-type-dialog.component';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostAccountingTypeState } from '../../../services/facades/master-data/cost-accounting-type-state.model';
import { CostAccountingTypeFacade } from '../../../services/facades/master-data/cost-accounting-type.facade';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';

@Component({
    templateUrl: './cost-accounting-type.page.html',
    styleUrls: [ './cost-accounting-type.page.css' ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostAccountingTypePage implements OnInit, OnDestroy {

    public dataSource: MatTableDataSource<ICostAccountingType> = new MatTableDataSource<ICostAccountingType>([]);
    public costAccountingTypeState$: Observable<ICostAccountingTypeState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myCostAccountingTypeFacade: CostAccountingTypeFacade,
                private mySharedDataFacade: SharedDataFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.costAccountingTypeState$ = this.myCostAccountingTypeFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => Object.values(s.costAccountingTypeLookup)),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((costAccountingTypes: ICostAccountingType[]) => {
                this.dataSource.data = costAccountingTypes;
            });

        void firstValueFrom(this.mySharedDataFacade.fetchCostAccountingTypes());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim().toLowerCase(); // Remove whitespace, MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public onOpenDialog(): void {
        const dialog: MatDialogRef<CostAccountingTypeDialogComponent> = this.myMatDialog.open(CostAccountingTypeDialogComponent, {
            width: '600px',
            data: {}
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostAccountingTypes(true));
            }
        });
    }

    public onDeleteCostAccountingType(costAccountingType: ICostAccountingType): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-accounting-type.confirm-delete.label',
                messageParams: { name: costAccountingType.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostAccountingTypeFacade.deleteCostAccountingType(costAccountingType.id);
                void firstValueFrom(this.mySharedDataFacade.fetchCostAccountingTypes(true));
            }
        });
    }

}
